import * as React  from 'react';
import { useSelector } from 'react-redux';
import { MiddlePanelConstant } from "../../../containers/WorkkardDetail/constants";
import Icons from '../../../icons';
import { ActorAvatar, ActorName, useActor } from './actor';
import { combinedViewTime } from './dateKey';
import ActivityTitle from './activityTitle';
import CombinedViewFiles from './combinedViewFiles';
import NoteCustomFields from './noteCustomFields';
import { selectSessionContextOidc } from '@container/AdminPanel/features/users/usersSelectors';

/**
 * A note written against an Activity.
 *
 * This component used to render two unrelated things: the note, and a
 * resolution-change block built from action-history fields that rode along on
 * the same row. Resolution changes are now their own entry type, so this
 * renders only the note and the timeline no longer shows the same change twice.
 */
const ActivityNoteTemplate = (props) => {
    const sessionContext = useSelector(selectSessionContextOidc);
    const userView = sessionContext?.userView;
    const { data, isPrintPreview } = props;
    const note = data.note || {};
    const publisher = useActor(note.updated_by);
    const reviewer = useActor(note.reviewed_by);

    const getDescription = () => {
        if (!process.env.mercury_orgIds.split(",").includes(String(sessionContext.organizationView.Id))) {
            return (
                `<section class='noteDescriptionFormating InformationLetter editorNoteFormatting'>${note.body}</section>`
            );
        }
        return note.body;
    };

    // A note can be both reviewed and published; the banners are shown in the
    // order the two events actually happened.
    const banners = [];
    if (note.is_reviewed && note.reviewed_by > 0) {
        banners.push({ kind: "Reviewed", at: note.reviewed_date });
    }
    if (note.internal_to_public && note.organization_id == userView?.orgId) {
        banners.push({ kind: "Published", at: note.updated_date });
    }
    banners.sort((a, b) => (a.at || 0) - (b.at || 0));

    const privacyLabel = note.privacy === 'Private' ? 'Internal'
        : note.privacy === 'Personal' ? 'Draft'
        : note.privacy;

    const hasContent = !!note.body || (data.files && data.files.length > 0);
    if (!hasContent) {
        return null;
    }

    return (
        <div className="combinedListing detailListing">
            <span className="time_line_bar"></span>
            <div className="combinedCreatedDate detailCreatedDate">
                <ActorAvatar userId={data.actor_id} />
            </div>
            <div className="combinedRightBlock detailRightBlock resolutionChanged">
                <div className="combinedDescription detailDescription">
                    <div className="alignMiddle mb mb__8 flexWrap rowSpaceBetween">
                        <div className="flexGrow alignMiddle flexWrap detailActivity">
                            <ActorName userId={data.actor_id} inner="header" />
                            <span className="dateTime mr">
                                <label className="mrNone">{combinedViewTime(data.occurred_at)}</label>
                            </span>
                            {note.template_name == 'Time Demand' &&
                                <span className="status mr quickTooltip-right" data-quicktooltip="High priority"><Icons name="flag" className="icon" /></span>
                            }
                            added an activity note in
                            <ActivityTitle activity={data.activity} isPrintPreview={isPrintPreview} />
                        </div>

                        <div className="cv_block_status selfStart">
                            <span className={`status right ${(privacyLabel || '').toLowerCase()}`}>
                                {privacyLabel}
                            </span>
                        </div>
                    </div>

                    <div className="update_information">
                        {banners.map((banner) => (
                            <React.Fragment key={banner.kind}>
                                {banner.kind == "Reviewed" &&
                                <div className="reviewNoteAdded selfStart rowSpaceBetween">
                                    <Icons name="review" className="icon mr" />
                                    <div className="colFlex">
                                        <p className="alignMiddle">
                                            <label className="mr">{MiddlePanelConstant.Note.ReviewedByText}</label>
                                            <strong className="mr"><ActorName userId={note.reviewed_by} inner="header" /></strong>
                                            on {combinedViewTime(note.reviewed_date)}
                                        </p>
                                        <em>{note.review_reason}</em>
                                    </div>
                                </div>}

                                {banner.kind == "Published" &&
                                <div className="publishNotesAdded alignMiddle">
                                    <Icons name="public" className="icon mr" />
                                    <label>{MiddlePanelConstant.Note.PublishedByText}
                                        <span className="mr black">{publisher.name}</span>
                                        from Internal to Public on {combinedViewTime(note.updated_date)}
                                    </label>
                                </div>}
                            </React.Fragment>
                        ))}
                        {note.template_name && <span className="mb__8 note_tmpl_type"><label>{note.template_name}</label></span>}
                        <NoteCustomFields fields={note.custom_fields} noteId={note.id} tzData={sessionContext.tzData} />

                        <div className="clr"></div>
                        {note.body &&
                            <div className='noteEmailFormating mb' dangerouslySetInnerHTML={{__html: getDescription()}}></div>
                        }
                        <CombinedViewFiles files={data.files} entry={data} isPrintPreview={isPrintPreview} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ActivityNoteTemplate;
